import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { PharmacIcon } from '@pharmac/ui'
import React from 'react'
import { MediaQuery } from '../../hooks/useBreakpoints'
import { ContentImagesDto } from '../../types/ContentImagesDto'
import { MultilingualKeyDto } from '../../types/MultilingualKeyDto'

export const ItemRoot = styled.div<{ isShop?: boolean }>`
  position: relative;
  display: grid;

  width: 100%;

  grid-template-columns: 170px 100fr 100px;
  grid-template-rows: ${(p) => (p.isShop ? '15px 25px 100px 25px' : '30px 40px 65px 25px')};

  border-radius: var(--border-radius-tiny);

  background-color: var(--color-lighter-than-lightest);

  margin-top: var(--spacing-small);

  &:first-of-type {
    margin-top: 0;
  }

  padding: var(--spacing-small);

  ${MediaQuery.maxMobile} {
    grid-template-columns: repeat(1, 1fr);
    grid-template-rows: ${(p) => (p.isShop ? '1fr 250px 40px' : '1fr 400px 40px')};

    border: 1px solid var(--color-accent-4);
    box-shadow: 0 1px 3px var(--color-accent-3);
  }
`

export const ItemImage = styled.div<{ isShop?: boolean }>`
  grid-area: 1 / 1 / 5 / 2;
  position: relative;
  overflow: hidden;

  img {
    width: 160px;
    height: ${(p) => (p.isShop ? '165px' : '160px')};
    object-fit: cover;
    border-radius: var(--border-radius-tiny);
  }

  cursor: pointer;

  ${MediaQuery.maxMobile} {
    grid-area: 2 / 1 / 3 / 3;

    img {
      width: 100%;
      height: ${(p) =>
        p.isShop
          ? 'calc(250px - (2 * var(--spacing-small)))'
          : 'calc(400px - (2 * var(--spacing-small)))'};
      border: 1px solid var(--color-accent-3);
    }
  }
`

const ItemRibbon = styled.div`
  background-color: var(--color-primary);
  position: absolute;
  left: -30%;
  top: 15%;
  transform: rotate(-45deg);
  width: 100%;
  height: 20px;
  text-align: center;
  color: var(--color-white);
  font-family: var(--font-family-primary);
`

export const ItemLabels = styled.div<{ hasActions?: boolean }>`
  grid-area: 1 / 2 / 2 / ${(p) => (p.hasActions ? 3 : 4)};

  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;

  > div {
    &:not(:last-of-type) {
      margin-right: var(--spacing-small);
    }
  }

  ${MediaQuery.maxMobile} {
    grid-area: 1 / 1 / 2 / 2;

    flex-direction: row;
    align-items: flex-start;
    padding-bottom: var(--spacing-tiny);

    > * {
      &:not(:first-of-type) {
        padding: 0 var(--spacing-small) 0 var(--spacing-small);
        margin-top: var(--spacing-tiny);
      }
    }
  }
`
export const ItemTitle = styled.div<{ hasNavigation?: boolean; hasMobileLabels?: boolean }>`
  grid-area: 2 / 2 / 3 / 4;

  font-size: 18px;
  font-weight: bold;
  line-height: var(--typo-text-medium-line-height);

  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;

  ${({ hasNavigation }) =>
    hasNavigation &&
    css`
      cursor: pointer;
    `}

  ${MediaQuery.maxMobile} {
    grid-area: ${({ hasMobileLabels }) => (hasMobileLabels ? '4 / 1 / 4 / 3' : '3 / 1 / 4 / 3')};
  }
`
export const ItemActions = styled.div`
  grid-area: 1 / 3 / 3 / 4;

  button {
    transition: var(--transition-cubic-bezier);
    :hover {
      scale: 1.1;
    }
  }

  ${MediaQuery.maxMobile} {
    grid-area: 1 / 2 / 2 / 3;
  }
`
export const ItemContents = styled.div<{ hasNavigation?: boolean }>`
  grid-area: 3 / 2 / 4 / 4;
  overflow: hidden;

  ${({ hasNavigation }) =>
    hasNavigation &&
    css`
      cursor: pointer;
    `}

  ${MediaQuery.maxMobile} {
    display: none;
  }
`

export const ItemFooter = styled.div`
  grid-area: 4 / 2 / 5 / 4;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`

export const MobileLabels = styled.div`
  grid-area: 3 / 1 / 5 / 5;

  ${MediaQuery.minDesktop} {
    display: none;
  }
`

export const MobileItemContents = styled.div`
  grid-area: 5 / 1 / 6 / 4;

  ${MediaQuery.minDesktop} {
    display: none;
  }
`

export const ItemFooterUser = styled.div``
export const ItemFooterCreatedAt = styled.div``
export const ItemFooterUpdatedAt = styled.div``

const HiddenItemOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  z-index: -1;

  svg {
    transform: translateY(10%);
    color: var(--color-accent-5);
  }
`

export type ExtranetListItemProps = {
  image?: ContentImagesDto
  bannerImage?: MultilingualKeyDto
  labels?: string[] | JSX.Element[]
  mobileLabels?: string[] | JSX.Element[]
  title?: string | JSX.Element
  content?: string | JSX.Element
  mobileContent?: string | JSX.Element
  actions?: JSX.Element[]
  navigateFn?: () => void
  ariaDescription?: string
  isHidden?: boolean
  hasRibbon?: boolean
  ribbonText?: string
  border?: boolean
  footerContent?: string | number | JSX.Element
  hasMobileLabels?: boolean
  isShop?: boolean
}

export const ExtranetListItem = ({
  image,
  labels,
  title,
  actions,
  content,
  mobileContent,
  navigateFn,
  ariaDescription,
  isHidden,
  hasRibbon,
  ribbonText,
  footerContent,
  bannerImage,
  border,
  mobileLabels,
  hasMobileLabels,
  isShop,
}: ExtranetListItemProps) => {
  const hasNavigation = typeof navigateFn === 'function' && typeof ariaDescription === 'string'

  if (typeof navigateFn === 'function' && typeof ariaDescription === 'undefined') {
    console.warn('ExtranetListItem titled ', title, 'does not have ariaDescription prop')
  }

  const ribbon = hasRibbon && !!ribbonText ? ribbonText : false

  return (
    <ItemRoot
      isShop={isShop}
      style={{
        opacity: isHidden ? 0.7 : 1,
        border: border ? '2px solid var(--color-accent-4)' : undefined,
      }}
    >
      {isHidden && (
        <HiddenItemOverlay>
          <PharmacIcon
            icon="eyeOff"
            size="70px"
          />
        </HiddenItemOverlay>
      )}
      <ItemImage
        isShop={isShop}
        onClick={hasNavigation ? navigateFn : undefined}
        role="link"
        aria-roledescription={ariaDescription}
      >
        {ribbon && <ItemRibbon>{ribbon}</ItemRibbon>}
        <img
          src={image?.desktop || image?.mobile || bannerImage?.fi}
          alt={''} // @todo: fix this
        />
      </ItemImage>
      <ItemLabels hasActions={!!actions && actions.length > 0}>
        {labels &&
          labels.map((a, i) => (
            <React.Fragment key={'extranetListItemLabel-i-' + i}>{a}</React.Fragment>
          ))}
      </ItemLabels>
      <MobileLabels>{mobileLabels}</MobileLabels>
      <ItemTitle
        hasMobileLabels={hasMobileLabels}
        hasNavigation={hasNavigation}
        onClick={hasNavigation ? navigateFn : undefined}
        role="link"
        aria-roledescription={ariaDescription}
      >
        {title}
      </ItemTitle>
      <MobileItemContents>{mobileContent}</MobileItemContents>
      <ItemActions>
        {actions &&
          actions.map((a, i) => (
            <React.Fragment key={'extranetListItemAction-i-' + i}>{a}</React.Fragment>
          ))}
      </ItemActions>
      <ItemContents
        hasNavigation={hasNavigation}
        onClick={hasNavigation ? navigateFn : undefined}
        role="link"
        aria-roledescription={ariaDescription}
      >
        {content}
      </ItemContents>
      <ItemFooter>{!!footerContent && footerContent}</ItemFooter>
    </ItemRoot>
  )
}
